import { useState, type FormEvent } from 'react';
import { AlertTriangle, Loader2, Printer } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ReprintInput, ReprintReason } from '@/api/qrCode.api';
import { useT, type MessageKey } from '@/lib/i18n';

const REPRINT_REASONS: Array<{ value: ReprintReason; label: MessageKey }> = [
  { value: 'DAMAGED', label: 'reprint.reason.DAMAGED' },
  { value: 'LOST', label: 'reprint.reason.LOST' },
  { value: 'PRINTER_JAM', label: 'reprint.reason.PRINTER_JAM' },
  { value: 'OTHER', label: 'reprint.reason.OTHER' },
];

interface ReprintReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** How many wristbands this reprint covers — shown in the warning. */
  count: number;
  submitting?: boolean;
  onConfirm: (input: ReprintInput) => void;
}

/**
 * A wristband prints once. A reprint — ticketbox only — mints a NEW QR, so the
 * old wristband and the visitor's e-ticket stop working at every gate. The
 * cashier has to say why; the server audits it.
 */
export function ReprintReasonDialog({
  open,
  onOpenChange,
  count,
  submitting,
  onConfirm,
}: ReprintReasonDialogProps) {
  const { t } = useT();
  const [reason, setReason] = useState<ReprintReason | null>(null);
  const [note, setNote] = useState('');

  const reset = () => {
    setReason(null);
    setNote('');
  };

  const noteRequired = reason === 'OTHER';
  const valid = reason !== null && (!noteRequired || note.trim().length >= 3);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || !reason) return;
    onConfirm({ reason, note: note.trim() || undefined });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-primary" />
            {count > 1 ? t('reprint.titleMany', { count }) : t('reprint.title')}
          </DialogTitle>
          <DialogDescription>{t('reprint.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {t('reprint.warn1')}<strong>{t('reprint.warn2')}</strong>{t('reprint.warn3')}{' '}
            <strong>{t('reprint.warn4')}</strong>{t('reprint.warn5')}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-2" role="radiogroup" aria-label={t('reprint.reasonsLabel')}>
            {REPRINT_REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                role="radio"
                aria-checked={reason === r.value}
                onClick={() => setReason(r.value)}
                className={cn(
                  'rounded-md border-2 px-3 py-2.5 text-left text-sm font-medium transition-colors',
                  reason === r.value
                    ? 'border-primary bg-primary/5 text-ink-900'
                    : 'border-border text-ink-600 hover:border-primary/40',
                )}
              >
                {t(r.label)}
              </button>
            ))}
          </div>

          {reason && (
            <div className="space-y-1.5">
              <Label htmlFor="reprint-note">
                {t('reprint.note')}{noteRequired ? '' : t('reprint.noteOptional')}
              </Label>
              <Input
                id="reprint-note"
                autoComplete="off"
                maxLength={200}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={noteRequired ? t('reprint.notePlaceholder') : ''}
              />
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="h-12 w-full gap-2"
            disabled={!valid || Boolean(submitting)}
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Printer className="h-5 w-5" />}
            {t('reprint.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
